/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Wizard Step 3: Outlines Tree Editor Component (Task 10.2.4 & 10.7.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useLongformWizardStore,
  type ArticleHeading,
} from '../../stores/useLongformWizardStore';
import { useI18n } from '../../stores/useI18nStore';
import {
  GripVertical,
  Plus,
  Trash2,
  Sparkles,
  RotateCcw,
  ArrowRight,
  ArrowLeft,
  ListTree,
  CornerDownRight,
  HelpCircle,
} from 'lucide-react';

interface SortableItemProps {
  heading: ArticleHeading;
  onUpdate: (id: string, updates: Partial<ArticleHeading>) => void;
  onDelete: (id: string) => void;
  onAddSub: (h2Id: string) => void;
  onEnrich: (h2Id: string) => void;
}

const SortableHeadingItem: React.FC<SortableItemProps> = ({
  heading,
  onUpdate,
  onDelete,
  onAddSub,
  onEnrich,
}) => {
  const { t } = useI18n();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: heading.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.6 : 1,
  };

  const isH3 = heading.level === 3;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-2 p-2.5 rounded-xl border transition-all duration-150 ${
        isH3
          ? 'ml-7 border-accent/20 bg-accent/5 hover:border-accent/40'
          : 'border-surface-border bg-surface hover:border-surface-border/80 shadow-sm'
      }`}
    >
      {/* Drag handle */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="p-1 rounded text-foreground-muted hover:text-foreground hover:bg-muted cursor-grab active:cursor-grabbing transition-colors"
        title={t('wizard.step3Details.dragHint')}
      >
        <GripVertical className="w-4 h-4" />
      </button>

      {/* Level badge */}
      {isH3 ? (
        <div className="flex items-center gap-1 text-accent flex-shrink-0">
          <CornerDownRight className="w-3.5 h-3.5" />
          <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-accent/10 border border-accent/20">
            H3
          </span>
        </div>
      ) : (
        <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-primary/10 border border-primary/20 text-primary flex-shrink-0">
          H2
        </span>
      )}

      {/* Editable Title Input */}
      <input
        type="text"
        value={heading.title}
        onChange={(e) => onUpdate(heading.id, { title: e.target.value })}
        className="flex-1 bg-transparent border-none text-xs font-semibold text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40 rounded px-2 py-1"
      />

      {/* Estimated Words Badge */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <input
          type="number"
          min={50}
          max={2000}
          step={50}
          value={heading.estimatedWords ?? 250}
          onChange={(e) =>
            onUpdate(heading.id, {
              estimatedWords: Math.max(50, parseInt(e.target.value, 10) || 100),
            })
          }
          className="w-16 px-1.5 py-0.5 text-[10px] font-mono text-right rounded border border-surface-border bg-surface-elevated text-foreground"
        />
        <span className="text-[10px] text-foreground-muted">{t('editor.words')}</span>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {!isH3 && (
          <>
            {/* Enrich with AI Button */}
            <button
              type="button"
              onClick={() => onEnrich(heading.id)}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold text-accent hover:bg-accent/10 transition-colors"
              title={t('wizard.step3Details.enrichAiBtn')}
            >
              <Sparkles className="w-3 h-3" />
              <span className="hidden sm:inline">{t('wizard.step3Details.enrichAiBtn')}</span>
            </button>

            {/* Add Sub H3 Button */}
            <button
              type="button"
              onClick={() => onAddSub(heading.id)}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold text-primary hover:bg-primary/10 transition-colors"
              title={t('wizard.step3Details.addH3Btn')}
            >
              <Plus className="w-3 h-3" />
              <span className="hidden md:inline">H3</span>
            </button>
          </>
        )}

        {/* Delete Button */}
        <button
          type="button"
          onClick={() => onDelete(heading.id)}
          className="p-1.5 rounded-lg text-foreground-muted hover:text-red-500 hover:bg-red-500/10 transition-colors"
          title={t('common.delete')}
          aria-label={t('common.delete')}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export const Step3OutlineView: React.FC = () => {
  const {
    headings,
    setHeadings,
    addHeading,
    updateHeading,
    deleteHeading,
    enrichSection,
    generateOutlines,
    isGeneratingOutlines,
    setStep,
    markStepCompleted,
    getSelectedFinalTitle,
  } = useLongformWizardStore();
  const { t } = useI18n();

  const [newH2Title, setNewH2Title] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 4,
      },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = headings.findIndex((h) => h.id === active.id);
      const newIndex = headings.findIndex((h) => h.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        setHeadings(arrayMove(headings, oldIndex, newIndex));
      }
    }
  };

  const handleAddH2 = () => {
    if (!newH2Title.trim()) return;
    addHeading(newH2Title.trim(), 2);
    setNewH2Title('');
  };

  const handleAddSubH3 = (parentH2Id: string) => {
    addHeading('Sub-section H3', 3, parentH2Id);
  };

  const handleContinueToStep4 = () => {
    if (headings.length < 2) return;
    markStepCompleted(3);
    setStep(4);
  };

  // Stats calculation
  const totalH2 = headings.filter((h) => h.level === 2).length;
  const totalH3 = headings.filter((h) => h.level === 3).length;
  const totalWords = headings.reduce((sum, h) => sum + (h.estimatedWords || 200), 0);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* 1. Header with Stats & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl border border-surface-border bg-surface shadow-glass">
        <div>
          <div className="flex items-center gap-2">
            <ListTree className="w-5 h-5 text-primary" />
            <h3 className="font-heading font-bold text-lg text-foreground">
              {t('wizard.step3Details.sectionTitle')}
            </h3>
          </div>
          <p className="text-xs text-foreground-muted mt-1">
            {t('editor.untitled')}: <strong className="text-foreground">{getSelectedFinalTitle()}</strong>
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={isGeneratingOutlines}
            onClick={generateOutlines}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-surface-border bg-surface-elevated text-xs font-bold hover:bg-muted text-foreground transition-colors disabled:opacity-50"
            title={t('wizard.step3Details.resetOutlinesBtn')}
          >
            <RotateCcw className={`w-3.5 h-3.5 ${isGeneratingOutlines ? 'animate-spin' : ''}`} />
            <span>{isGeneratingOutlines ? t('common.loading') : t('wizard.step3Details.resetOutlinesBtn')}</span>
          </button>
        </div>
      </div>

      {/* 2. Overview Stats & Helper Banner */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-3.5 rounded-xl border border-primary/20 bg-primary/5 text-xs">
        <div className="flex items-center gap-4">
          <span className="font-bold text-foreground">
            {totalH2} <span className="font-normal text-foreground-muted">H2</span>
          </span>
          <span>•</span>
          <span className="font-bold text-foreground">
            {totalH3} <span className="font-normal text-foreground-muted">H3</span>
          </span>
          <span>•</span>
          <span className="font-mono text-primary font-bold">
            ~{totalWords.toLocaleString()} <span className="font-normal text-foreground-muted">{t('editor.words')}</span>
          </span>
        </div>

        <div className="flex items-center gap-1.5 text-foreground-muted text-[11px]">
          <HelpCircle className="w-3.5 h-3.5 text-accent" />
          <span>{t('wizard.step3Details.sectionDesc')}</span>
        </div>
      </div>

      {/* 3. Sortable Headings List */}
      <div className="space-y-2.5">
        {headings.length === 0 ? (
          <div className="p-8 text-center rounded-2xl border border-surface-border bg-surface">
            <ListTree className="w-8 h-8 text-primary mx-auto mb-2 opacity-50" />
            <p className="text-sm font-medium text-foreground">{t('wizard.step3Details.resetOutlinesBtn')}</p>
            <button
              type="button"
              onClick={generateOutlines}
              className="mt-3 px-4 py-2 rounded-xl bg-primary text-primary-fg text-xs font-bold"
            >
              {t('wizard.step3Details.resetOutlinesBtn')}
            </button>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={headings.map((h) => h.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {headings.map((heading) => (
                  <SortableHeadingItem
                    key={heading.id}
                    heading={heading}
                    onUpdate={updateHeading}
                    onDelete={deleteHeading}
                    onAddSub={handleAddSubH3}
                    onEnrich={enrichSection}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {/* Add new H2 Section Bar */}
        <div className="flex gap-2 pt-2">
          <input
            type="text"
            value={newH2Title}
            onChange={(e) => setNewH2Title(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddH2();
              }
            }}
            placeholder={t('wizard.step3Details.addH2Btn')}
            className="flex-1 px-4 py-2.5 rounded-xl border border-surface-border bg-surface-elevated text-xs text-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all placeholder:text-foreground-muted/60"
          />
          <button
            type="button"
            onClick={handleAddH2}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-surface-border bg-surface text-foreground hover:bg-muted text-xs font-bold transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>{t('wizard.step3Details.addH2Btn')}</span>
          </button>
        </div>
      </div>

      {/* 4. Navigation Footer */}
      <div className="flex items-center justify-between gap-3 pt-3">
        <button
          type="button"
          onClick={() => setStep(2)}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-surface-border bg-surface-elevated text-xs font-bold hover:bg-muted text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('common.back')}</span>
        </button>

        <button
          type="button"
          disabled={headings.length < 2}
          onClick={handleContinueToStep4}
          className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary text-primary-fg text-xs font-bold hover:bg-primary-hover shadow-glow-primary/20 transition-all active:scale-95 disabled:opacity-50"
        >
          <span>{t('wizard.step3Details.continueBtn')}</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
